import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  LightBulbIcon,
  ChartBarIcon,
  CogIcon
} from '@heroicons/react/24/outline';

interface ConsultationFormData {
  name: string;
  email: string;
  company: string;
  businessSize: string;
  industry: string;
  currentChallenges: string;
  aiExperience: string;
  consultationType: string;
  timeline: string;
  budget: string;
  additionalInfo: string;
}

const Consultation: React.FC = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<ConsultationFormData>({
    name: '',
    email: '',
    company: '',
    businessSize: '',
    industry: '',
    currentChallenges: '',
    aiExperience: '',
    consultationType: '',
    timeline: '',
    budget: '',
    additionalInfo: ''
  });

  const totalSteps = 4;

  const consultationTypes = [
    { 
      id: 'strategy', 
      title: t('consultation.types.strategy.title'),
      description: t('consultation.types.strategy.description'),
      icon: LightBulbIcon
    },
    { 
      id: 'assessment', 
      title: t('consultation.types.assessment.title'),
      description: t('consultation.types.assessment.description'),
      icon: ChartBarIcon
    },
    { 
      id: 'implementation', 
      title: t('consultation.types.implementation.title'),
      description: t('consultation.types.implementation.description'),
      icon: CogIcon
    },
    { 
      id: 'optimization', 
      title: t('consultation.types.optimization.title'),
      description: t('consultation.types.optimization.description'),
      icon: UserGroupIcon
    }
  ];

  const businessSizes = [
    { id: 'startup', label: t('consultation.businessSizes.startup') },
    { id: 'small', label: t('consultation.businessSizes.small') },
    { id: 'medium', label: t('consultation.businessSizes.medium') },
    { id: 'large', label: t('consultation.businessSizes.large') }
  ];

  const industries = [
    t('consultation.industries.technology'), 
    t('consultation.industries.healthcare'), 
    t('consultation.industries.finance'), 
    t('consultation.industries.manufacturing'), 
    t('consultation.industries.retail'),
    t('consultation.industries.logistics'), 
    t('consultation.industries.education'), 
    t('consultation.industries.realEstate'), 
    t('consultation.industries.professionalServices'), 
    t('consultation.industries.other')
  ];

  const handleInputChange = (field: keyof ConsultationFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/admin/consultations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          type: 'consultation',
          submittedAt: new Date().toISOString()
        }),
      });

      // Debug logging for Netlify issues
      try {
        const clone = response.clone();
        const text = await clone.text();
        console.log('[Consultation Submit] status', response.status, 'body', text);
      } catch (e) {
        console.log('[Consultation Submit] response logging failed');
      }

      if (response.ok) {
        setIsSuccess(true);
        try {
          // Dispatch a reminder push/email for follow-up task creation
          await fetch('/api/admin/reminders/dispatch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: 'New consultation submitted',
              body: `${formData.name} requested a consultation`,
              url: '/admin?tab=calendar',
              tag: 'consultation-new'
            })
          });
        } catch {}
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Consultation submission error:', error);
      alert('Failed to submit consultation request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', email: '', company: '', businessSize: '', industry: '',
      currentChallenges: '', aiExperience: '', consultationType: '',
      timeline: '', budget: '', additionalInfo: ''
    });
    setCurrentStep(1);
    setIsSuccess(false);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center bg-gray-800 border border-gray-700 rounded-2xl p-8"
        >
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-4">{t('consultation.success.title', 'Consultation Booked!')}</h1>
          <p className="text-gray-300 mb-6">
            {t('consultation.success.message', 'Thank you for your consultation request. I\'ll review your requirements and get back to you within 24 hours to schedule our discussion.')}
          </p>
          <button
            onClick={resetForm}
            className="btn-primary"
          >
            {t('consultation.success.newConsultation', 'Book Another Consultation')}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
            {t('consultation.title', 'Free AI Consultation')}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('consultation.description', 'Get a personalized consultation to discover how AI can revolutionize your business operations and drive growth.')}
          </p>
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-gray-400">
              {t('contact.step')} {currentStep} {t('contact.of')} {totalSteps}
            </span>
            <span className="text-sm text-gray-400">
              {Math.round((currentStep / totalSteps) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-gray-800 border border-gray-700 rounded-2xl p-8"
        >
          {/* Step 1: Business Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">{t('consultation.step1.title')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('consultation.step1.fullName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('consultation.step1.fullNamePlaceholder')}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('consultation.step1.email')} *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('consultation.step1.emailPlaceholder')}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('consultation.step1.company')} *
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t('consultation.step1.companyPlaceholder')}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('consultation.step1.industry')} *
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('consultation.step1.industryPlaceholder')}</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('consultation.step1.businessSize')} *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {businessSizes.map(size => (
                    <label key={size.id} className="flex items-center p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                      <input
                        type="radio"
                        name="businessSize"
                        value={size.id}
                        checked={formData.businessSize === size.id}
                        onChange={(e) => handleInputChange('businessSize', e.target.value)}
                        className="text-primary-500 focus:ring-primary-500"
                      />
                      <span className="ml-3 text-white">{size.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Consultation Type */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">{t('consultation.step2.title')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {consultationTypes.map(type => {
                  const Icon = type.icon;
                  return (
                    <label 
                      key={type.id} 
                      className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.consultationType === type.id 
                          ? 'border-primary-500 bg-primary-500/10' 
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="consultationType"
                        value={type.id}
                        checked={formData.consultationType === type.id}
                        onChange={(e) => handleInputChange('consultationType', e.target.value)}
                        className="sr-only"
                      />
                      <Icon className="w-8 h-8 text-primary-400 mb-3" />
                      <h3 className="text-lg font-semibold text-white mb-2">{type.title}</h3>
                      <p className="text-gray-300 text-sm">{type.description}</p>
                    </label>
                  );
                })}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('consultation.step2.currentChallenges')}
                </label>
                <textarea
                  value={formData.currentChallenges}
                  onChange={(e) => handleInputChange('currentChallenges', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('consultation.step2.challengesPlaceholder')}
                />
              </div>
            </div>
          )}

          {/* Step 3: Experience and Timeline */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">{t('consultation.step3.title')}</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('consultation.step3.aiExperience')}
                </label>
                <select
                  value={formData.aiExperience}
                  onChange={(e) => handleInputChange('aiExperience', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">{t('consultation.experienceLevels.none')}</option>
                  <option value="none">{t('consultation.experienceLevels.none')}</option>
                  <option value="basic">{t('consultation.experienceLevels.basic')}</option>
                  <option value="intermediate">{t('consultation.experienceLevels.intermediate')}</option>
                  <option value="advanced">{t('consultation.experienceLevels.advanced')}</option>
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('consultation.step3.timeline')}
                  </label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t('consultation.timelines.immediate')}</option>
                    <option value="immediate">{t('consultation.timelines.immediate')}</option>
                    <option value="short">{t('consultation.timelines.short')}</option>
                    <option value="medium">{t('consultation.timelines.medium')}</option>
                    <option value="long">{t('consultation.timelines.long')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('consultation.step3.budgetRange')}
                  </label>
                  <select
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t('consultation.budgets.under10k')}</option>
                    <option value="under-10k">{t('consultation.budgets.under10k')}</option>
                    <option value="10k-25k">{t('consultation.budgets.10k25k')}</option>
                    <option value="25k-50k">{t('consultation.budgets.25k50k')}</option>
                    <option value="50k-100k">{t('consultation.budgets.50k100k')}</option>
                    <option value="over-100k">{t('consultation.budgets.over100k')}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Additional Information */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-6">{t('consultation.step4.title')}</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('consultation.step4.additionalInfo')}
                </label>
                <textarea
                  value={formData.additionalInfo}
                  onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('consultation.step4.additionalInfoPlaceholder')}
                />
              </div>
              
              {/* Summary */}
              <div className="bg-gray-700/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">{t('consultation.step4.summary')}</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-400">{t('consultation.step4.companySummary')}</span> <span className="text-white">{formData.company}</span></p>
                  <p><span className="text-gray-400">{t('consultation.step4.industrySummary')}</span> <span className="text-white">{formData.industry}</span></p>
                  <p><span className="text-gray-400">{t('consultation.step4.sizeSummary')}</span> <span className="text-white">{businessSizes.find(s => s.id === formData.businessSize)?.label}</span></p>
                  <p><span className="text-gray-400">{t('consultation.step4.typeSummary')}</span> <span className="text-white">{consultationTypes.find(t => t.id === formData.consultationType)?.title}</span></p>
                  <p><span className="text-gray-400">{t('consultation.step4.timelineSummary')}</span> <span className="text-white">{formData.timeline}</span></p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-700">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                currentStep === 1 
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              {t('contact.previous')}
            </button>
            
            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                className="btn-primary flex items-center gap-2"
              >
                {t('contact.next')}
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('contact.submitting')}
                  </>
                ) : (
                  <>
                    <CalendarDaysIcon className="w-4 h-4" />
                    {t('consultation.submit', 'Schedule Consultation')}
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Consultation;